import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { orderService } from "../../../services/api/orderService";
import PaymentHistoryPresenter from "./PaymentHistoryPresenter";
import { useDebouncedCallback } from "../../../hooks/useDebounce";

const PaymentHistory = () => {
  const { token_client } = useContext(AuthContext);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [copiedInvoice, setCopiedInvoice] = useState(false);

  const fetchOrders = useCallback(async (search = "", page = currentPage) => {
    setLoading(true);
    try {
      const response = await orderService.get_list_user_order(token_client, {
        page: page,
        limit: 10,
        search_value: search,
      });
      setOrders(response.data || []);
      setPagination(
        response.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 },
      );
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      toast.error("Có lỗi xảy ra khi tải lịch sử thanh toán");
    } finally {
      setLoading(false);
    }
  }, [token_client, currentPage]);

  const executeSearch = useCallback(
    (search) => {
      setCurrentPage(1);
      fetchOrders(search, 1);
    },
    [fetchOrders],
  );
  const {
    run: debouncedFetchOrders,
    flush: flushSearchOrders,
  } = useDebouncedCallback(executeSearch, 500);

  useEffect(() => {
    fetchOrders(searchTerm, currentPage);
  }, [currentPage, token_client]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFetchOrders(value);
  };

  const handleSearchClick = (searchValue = searchTerm) =>
    flushSearchOrders(String(searchValue ?? "").trim());

  const handleViewDetail = (order) => {
    setSelectedOrder(order);
    setShowDetail(true);
  };

  const handleCopyInvoice = () => {
    if (selectedOrder?.invoice) {
      navigator.clipboard.writeText(selectedOrder.invoice);
      setCopiedInvoice(true);
      setTimeout(() => setCopiedInvoice(false), 2000);
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <PaymentHistoryPresenter
      orders={orders}
      pagination={pagination}
      loading={loading}
      searchTerm={searchTerm}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      handleSearchChange={handleSearchChange}
      handleSearchClick={handleSearchClick}
      handleViewDetail={handleViewDetail}
      showDetail={showDetail}
      setShowDetail={setShowDetail}
      selectedOrder={selectedOrder}
      copiedInvoice={copiedInvoice}
      handleCopyInvoice={handleCopyInvoice}
      formatCurrency={formatCurrency}
      formatDate={formatDate}
      navigate={navigate}
    />
  );
};

export default PaymentHistory;
